import React, { useState, useEffect, useRef, useCallback } from 'react';
import { formatPhoneForDisplay } from '../../../utils/phoneUtils';
import ResetPatientPasswordModal, { IconKey } from './ResetPatientPasswordModal';
import './PatientProfileLayout.css';

const IconPhone = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"
      stroke="currentColor"
      strokeWidth="1.9"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconMail = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.9" />
    <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const IconEdit = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
  </svg>
);

const IconChevron = () => (
  <svg className="pdx-chev" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const IconPause = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <rect x="6" y="5" width="4" height="14" rx="1" />
    <rect x="14" y="5" width="4" height="14" rx="1" />
  </svg>
);

const IconPlay = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7 4.5v15a1 1 0 0 0 1.5.86l12.5-7.5a1 1 0 0 0 0-1.72L8.5 3.64A1 1 0 0 0 7 4.5z" />
  </svg>
);

const IconReset = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M3 12a9 9 0 1 0 3-6.7" />
    <polyline points="3 3 3 9 9 9" />
  </svg>
);

const CPT_DESCRIPTIONS = {
  '99453': 'Setup and education',
  '99454': 'Device supply, 16+ days',
  '99457': 'First 20 min review',
  '99445': '2–15 days readings',
  '99470': '10–19 min review',
  '99458': 'Additional 20 min',
  '99490': 'CCM — first 20 min per calendar month',
  '99439': 'CCM — additional 40 min',
  '99491': 'CCM — complex — first 30 min',
  '99437': 'CCM — complex — additional 60 min',
  '99487': 'Complex CCM — first 60 min',
  '99489': 'Complex CCM — additional 30 min',
  '99424': 'PCM Physician/QHP — first 30 min',
  '99425': 'PCM Physician/QHP — additional 30 min',
  '99426': 'PCM Clinical Staff — first 30 min',
  '99427': 'PCM Clinical Staff — additional 30 min',
};

const formatDateYMD = (dateString) => {
  if (!dateString) return '—';
  const match = String(dateString).trim().match(/^(\d{4})-(\d{2})-(\d{2})/);
  let date;
  if (match) {
    date = new Date(
      Number.parseInt(match[1], 10),
      Number.parseInt(match[2], 10) - 1,
      Number.parseInt(match[3], 10)
    );
  } else {
    date = new Date(dateString);
  }
  if (Number.isNaN(date.getTime())) return '—';
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${month}/${day}/${date.getFullYear()}`;
};

const getPatientAge = (dateString) => {
  if (!dateString) return null;
  const birth = new Date(dateString);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age -= 1;
  }
  return age >= 0 ? age : null;
};

const getMonitoringStatusLabel = (status) => {
  if (status == null || status === '') return 'Active';
  const normalized = String(status).trim().toLowerCase();
  const numericStatus = /^\d+$/.test(normalized) ? parseInt(normalized, 10) : null;
  if (numericStatus === 2 || normalized === 'active') return 'Active';
  if (numericStatus === 3 || normalized === 'pending') return 'Pending';
  if (numericStatus === 4 || normalized === 'locked' || normalized === 'lock') return 'Locked';
  if (normalized === 'paused' || normalized === 'inactive') return 'Inactive';
  return String(status);
};

const getInitials = (name) => {
  if (!name) return 'P';
  const parts = String(name).split(/[\s,]+/).filter(Boolean);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const getCptState = (pill) => {
  const variant = pill.variant || 'pending';
  let progress = pill.badge || '';
  if (!progress && pill.code === '99458' && pill.addonTierTarget) {
    progress = `${pill.addonTierProgress || 0} of ${pill.addonTierTarget} m`;
  }
  progress = String(progress).replace(/^(\d+)\s*\/\s*(\d+)\s*([dm])$/i, '$1 of $2 $3');
  if (variant === 'success') return { label: progress && progress !== '—' ? progress : '✓ Achieved', className: 'applied' };
  if (variant === 'warning' || variant === 'info') return { label: progress || 'In progress', className: 'warn' };
  return { label: progress || 'Pending', className: '' };
};

/**
 * Patient row at the top of the patient card: identity, session timer, CPT status
 * and a Details popover holding demographics, contacts and the care team.
 */
const PatientDemographicsBanner = ({
  patient,
  patientFullName,
  getGenderText,
  formatNameLastFirst,
  deviceSerial,
  showContactActions = true,
  onCall,
  onEmail,
  onEdit,
  practiceId,
  patientId,
  billingPeriodLabel,
  timer = 0,
  formatTimer,
  isRunning = false,
  startTimer,
  pauseTimer,
  openResetTimerModal,
  timerPillSetDisplay = [],
  activeProgram = 'RPM',
  isPatientSelfView = false,
  profilePicUrl = null,
}) => {
  const [openPanel, setOpenPanel] = useState(null);
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [profilePicFailed, setProfilePicFailed] = useState(false);
  const rowRef = useRef(null);
  const closeResetPassword = useCallback(() => setResetPasswordOpen(false), []);

  useEffect(() => {
    setProfilePicFailed(false);
  }, [profilePicUrl]);

  useEffect(() => {
    if (!openPanel) return undefined;
    const handlePointerDown = (event) => {
      if (rowRef.current && !rowRef.current.contains(event.target)) setOpenPanel(null);
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setOpenPanel(null);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [openPanel]);

  const togglePanel = (panel) => setOpenPanel((current) => (current === panel ? null : panel));

  const age = getPatientAge(patient?.date_of_birth);
  const genderText = getGenderText?.(patient?.gender) || patient?.gender || '—';
  const homePhone = formatPhoneForDisplay(patient?.phone_number || patient?.homePhone, '—');
  const cellPhone = formatPhoneForDisplay(
    patient?.cell_phone_number || patient?.cellPhone || patient?.phone_number,
    '—'
  );
  const emergencyContactName = [
    String(patient?.emergency_contact_first_name || patient?.emergencyContactFirstName || '').trim(),
    String(patient?.emergency_contact_last_name || patient?.emergencyContactLastName || '').trim()
  ].filter(Boolean).join(' ') || '—';
  const emergencyRelation = String(patient?.emergency_contact_relation || patient?.emergencyContactRelation || '').trim() || '—';
  const emergencyPhone = formatPhoneForDisplay(patient?.emergency_contact_phone || patient?.emergencyContactPhone, '—');
  const emergencyAddress = String(patient?.emergency_contact_address || patient?.emergencyContactAddress || '').trim() || '—';
  const addressParts = [
    patient?.full_address || patient?.address_line_1,
    patient?.city,
    patient?.zip_code,
  ].filter(Boolean);
  const address = addressParts.length ? addressParts.join(', ') : '—';
  const providerLabel =
    formatNameLastFirst?.(patient?.provider_name) || patient?.provider_name || '—';
  const practiceLabel = patient?.practice_name || '—';
  const serialLabel =
    String(
      deviceSerial ||
        patient?.device_serial ||
        patient?.deviceSerial ||
        patient?.serial_number ||
        ''
    ).trim() || '—';
  const caregiverLabel = (() => {
    const allCgs = [];
    [patient?.practice_caregivers, patient?.system_caregivers].forEach((list) => {
      if (!Array.isArray(list)) return;
      list.forEach((cg) => {
        const raw = cg.name || `${cg.first_name || ''} ${cg.last_name || ''}`;
        const n = formatNameLastFirst?.(raw) || raw;
        if (n && n.trim()) allCgs.push(n.trim());
      });
    });
    if (allCgs.length > 0) return [...new Set(allCgs)].join(', ');
    return formatNameLastFirst?.(patient?.caregiver_name) || patient?.caregiver_name || '—';
  })();
  const rpmStartLabel = formatDateYMD(patient?.rpm_start_date || patient?.rpmStartDate);
  const dobLabel = formatDateYMD(patient?.date_of_birth);

  const statusLabel = getMonitoringStatusLabel(patient?.status);
  const statusTone = {
    Active: 'active',
    Pending: 'pending',
    Locked: 'locked',
    Inactive: 'inactive',
  }[statusLabel] || 'inactive';
  const initials = getInitials(patientFullName);

  const metaParts = [
    genderText && genderText !== '—' ? genderText : null,
    age != null ? String(age) : null,
    dobLabel !== '—' ? `DOB ${dobLabel}` : null,
    cellPhone !== '—' ? cellPhone : null,
    serialLabel !== '—' ? `SN ${serialLabel}` : null,
  ].filter(Boolean);

  const rawPills = Array.isArray(timerPillSetDisplay) ? timerPillSetDisplay : [];
  const pills = String(activeProgram || '').toUpperCase() === 'RPM'
    ? ['99453', '99454', '99457', '99445', '99470', '99458']
        .map((code) => rawPills.find((pill) => pill.code === code))
        .filter(Boolean)
    : rawPills;
  const appliedCount = pills.filter((p) => (p.variant || 'pending') === 'success').length;
  const timerText = typeof formatTimer === 'function' ? formatTimer(timer) : timer;

  return (
    <div className="pdx-row pdx-patient-row" ref={rowRef}>
      {profilePicUrl && !profilePicFailed ? (
        <img
          className="pdx-avatar"
          src={profilePicUrl}
          alt={patientFullName ? `${patientFullName} profile` : 'Patient profile'}
          onError={() => setProfilePicFailed(true)}
        />
      ) : (
        <div className="pdx-avatar">{initials}</div>
      )}

      <div className="pdx-ident">
        <div>
          <span className="pdx-name">{patientFullName || 'Patient'}</span>{' '}
          <span className={`pdx-status pdx-status-${statusTone}`}>· {statusLabel}</span>
        </div>
        <div className="pdx-meta" title={metaParts.join(' · ')}>
          {metaParts.length ? metaParts.join(' · ') : '—'}
        </div>
      </div>

      <div className="pdx-patient-right">
        {!isPatientSelfView ? (
          <div className="pdx-session">
            <span className="pdx-session-label">Session time</span>
            <span className={`pdx-session-timer${isRunning ? '' : ' paused'}`}>{timerText}</span>
            <button
              type="button"
              className="pdx-plain-btn"
              onClick={isRunning ? pauseTimer : startTimer}
              title={isRunning ? 'Pause session' : 'Start session'}
              aria-label={isRunning ? 'Pause session' : 'Start session'}
            >
              {isRunning ? <IconPause /> : <IconPlay />}
            </button>
            <button
              type="button"
              className="pdx-plain-btn reset"
              onClick={openResetTimerModal}
              title="Reset session timer"
              aria-label="Reset session timer"
            >
              <IconReset />
            </button>
          </div>
        ) : null}

        {!isPatientSelfView ? (
          <button
            type="button"
            className={`pdx-chip${appliedCount > 0 ? ' pdx-chip-green' : ''}`}
            aria-expanded={openPanel === 'cpt'}
            onClick={() => togglePanel('cpt')}
          >
            {appliedCount}/{pills.length} CPT
          </button>
        ) : null}

        {showContactActions && onCall ? (
          <button type="button" className="pdx-chip icon" title="Call patient" aria-label="Call patient">
            <IconPhone />
          </button>
        ) : null}
        {showContactActions && onEmail ? (
          <button type="button" className="pdx-chip icon" onClick={onEmail} title="Send message" aria-label="Send message">
            <IconMail />
          </button>
        ) : null}

        <button
          type="button"
          className="pdx-chip"
          aria-expanded={openPanel === 'details'}
          onClick={() => togglePanel('details')}
        >
          Details
          <IconChevron />
        </button>
      </div>

      {openPanel === 'details' ? (
        <div className="pdx-popover pdx-details-panel" role="dialog" aria-label="Patient details">
          <div className="pdx-details-grid">
            <div>
              <div className="pdx-group-title">Patient</div>
              <dl className="pdx-kv">
                <dt>Gender / age</dt><dd>{genderText}{age != null ? ` · ${age}` : ''}</dd>
                <dt>Date of birth</dt><dd>{dobLabel}</dd>
                <dt>RPM start date</dt><dd>{rpmStartLabel}</dd>
                <dt>Device</dt><dd>{serialLabel}</dd>
                <dt>Billing period</dt><dd>{billingPeriodLabel || 'Current month'}</dd>
              </dl>
            </div>
            <div>
              <div className="pdx-group-title">Contact</div>
              <dl className="pdx-kv">
                <dt>Cell phone</dt><dd>{cellPhone}</dd>
                <dt>Home phone</dt><dd>{homePhone}</dd>
                <dt>Email</dt><dd>{patient?.email || '—'}</dd>
                <dt>Address</dt><dd>{address}</dd>
              </dl>
            </div>
            <div>
              <div className="pdx-group-title">Emergency contact</div>
              <dl className="pdx-kv">
                <dt>Name</dt><dd>{emergencyContactName}</dd>
                <dt>Relationship</dt><dd>{emergencyRelation}</dd>
                <dt>Phone</dt><dd>{emergencyPhone}</dd>
                <dt>Address</dt><dd>{emergencyAddress}</dd>
              </dl>
            </div>
          </div>
          <div className="pdx-details-foot">
            <div className="pdx-care-team">
              <span>Practice: <strong>{practiceLabel}</strong></span>
              <span>Provider: <strong>{providerLabel}</strong></span>
              <span>Caregiver: <strong>{caregiverLabel}</strong></span>
            </div>
            {typeof onEdit === 'function' ? (
              <div className="pdx-details-actions">
                <button
                  type="button"
                  className="pdx-chip"
                  onClick={() => {
                    setOpenPanel(null);
                    setResetPasswordOpen(true);
                  }}
                >
                  <IconKey />
                  Reset password
                </button>
                <button
                  type="button"
                  className="pdx-chip"
                  onClick={() => {
                    setOpenPanel(null);
                    onEdit();
                  }}
                >
                  <IconEdit />
                  Edit patient
                </button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <ResetPatientPasswordModal
        open={resetPasswordOpen}
        onClose={closeResetPassword}
        practiceId={practiceId}
        patientId={patientId}
        patientName={patientFullName}
        username={patient?.username || ''}
        practiceName={patient?.practice_name || ''}
        firstName={patient?.first_name || ''}
        lastName={patient?.last_name || ''}
      />

      {openPanel === 'cpt' ? (
        <div className="pdx-popover pdx-cpt-panel" role="dialog" aria-label="CPT codes this period">
          <div className="pdx-cpt-head">
            <div className="pdx-group-title">CPT codes this period</div>
            <span className="pdx-cpt-period">{billingPeriodLabel || 'Current month'}</span>
          </div>
          {pills.length > 0 ? (
            pills.map((pill) => {
              const state = getCptState(pill);
              return (
                <div className="pdx-cpt-row" key={pill.chipKey || pill.code}>
                  <span className="pdx-cpt-code">{pill.code}</span>
                  <span className="pdx-cpt-desc">{CPT_DESCRIPTIONS[pill.code] || ''}</span>
                  <span className={`pdx-cpt-state ${state.className}`}>{state.label}</span>
                </div>
              );
            })
          ) : (
            <div className="pdx-cpt-empty">No codes active</div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default PatientDemographicsBanner;
